// src/lib/storage/indexedDB.js — Módulo de IndexedDB para PropZyn
// ================================================================
// 🗄️ Reemplaza localStorage para datos de usuario, logs y archivos
// 📦 Usa idb (Jake Archibald) — misma librería que Notion/Figma/WhatsApp
// 🔥 V1 ENTERPRISE — Ultra rápido, ultra robusto, cero breaking changes
// ================================================================

import { openDB } from 'idb';

// ─── Constantes ─────────────────────────────────────────────────
const DB_NAME = 'propzyn_db';
const DB_VERSION = 1;
const STORES = {
  USER_DATA: 'user_data',
  SYNC_LOGS: 'sync_logs',
  FILES: 'files'
};

// ─── Configuración de rendimiento ──────────────────────────────
const MAX_LOG_ENTRIES = 50;
const MAX_LOG_AGE_DAYS = 7;
const FILE_CHUNK_SIZE = 1048576; // 1MB exacto
const MAX_FILE_CHUNKS = 50; // 50MB máximo por archivo

let dbInstance = null;
let dbInitPromise = null;

// ═══════════════════════════════════════════════════════════════
// 🏗️ INICIALIZACIÓN DE LA BASE DE DATOS (SINGLETON + LAZY)
// ═══════════════════════════════════════════════════════════════

/**
 * Abre (o crea) la base de datos IndexedDB con todas las stores necesarias.
 * 
 * 🔥 Patrón Singleton con Lazy Initialization y Promise Caching:
 *    - Evita múltiples conexiones simultáneas
 *    - Si se llama mientras se está abriendo, reusa la misma promesa
 *    - Igual que Notion/Figma/WhatsApp Web
 * 
 * Object Stores:
 *   - user_data (keyPath: 'id') → Datos principales del usuario
 *   - sync_logs (keyPath: 'id', autoIncrement) → Logs de sincronización
 *     • Índice 'userId' para búsquedas rápidas por usuario
 *     • Índice 'type' para filtrar por tipo de log
 *   - files (keyPath: 'chunkId') → Archivos base64 en chunks
 * 
 * @returns {Promise<IDBDatabase>} Instancia de la base de datos
 */
export async function getDB() {
  // Singleton: si ya está abierta, retornar inmediatamente
  if (dbInstance) return dbInstance;

  // Si ya hay una promesa de inicialización en curso, reusarla
  if (dbInitPromise) return dbInitPromise;

  dbInitPromise = openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      console.log(`🏗️ [IndexedDB] Upgrade: v${oldVersion} → v${newVersion}`);

      // Object Store para datos de usuario
      if (!db.objectStoreNames.contains(STORES.USER_DATA)) {
        db.createObjectStore(STORES.USER_DATA, { keyPath: 'id' });
        console.log('  ✅ user_data store creada');
      }

      // Object Store para logs de sincronización
      if (!db.objectStoreNames.contains(STORES.SYNC_LOGS)) {
        const logStore = db.createObjectStore(STORES.SYNC_LOGS, {
          keyPath: 'id',
          autoIncrement: true,
        });
        logStore.createIndex('userId', 'userId', { unique: false });
        logStore.createIndex('type', 'type', { unique: false });
        console.log('  ✅ sync_logs store creada');
      }

      // Object Store para archivos (chunks de 1MB)
      if (!db.objectStoreNames.contains(STORES.FILES)) {
        db.createObjectStore(STORES.FILES, { keyPath: 'chunkId' });
        console.log('  ✅ files store creada');
      }
    },
    blocked() {
      console.warn('⚠️ [IndexedDB] Bloqueada por otra pestaña. Cerrando...');
    },
    blocking() {
      // Si esta instancia está bloqueando a otras, cerrar para permitir upgrade
      dbInstance?.close();
      dbInstance = null;
      dbInitPromise = null;
    },
    terminated() {
      console.warn('⚠️ [IndexedDB] Conexión terminada inesperadamente');
      dbInstance = null;
      dbInitPromise = null;
    },
  });

  dbInstance = await dbInitPromise;
  dbInitPromise = null;

  console.log('✅ [IndexedDB] Base de datos inicializada correctamente');

  // Limpieza inicial de logs expirados (asíncrona, no bloqueante)
  cleanExpiredLogs().catch(() => {});

  return dbInstance;
}

// ═══════════════════════════════════════════════════════════════
// 👤 USER DATA — Reemplaza localStorage imoveispro_db_user_*
// ═══════════════════════════════════════════════════════════════

/**
 * Guarda los datos completos del usuario en IndexedDB.
 * 🔥 Equivalente directo a: localStorage.setItem(`imoveispro_db_user_${userId}`, JSON.stringify(data))
 * 
 * @param {string} userId - ID del usuario
 * @param {object} data - Datos completos (properties[], clients[], tasks[], etc.)
 * @returns {Promise<void>}
 */
export async function saveUserData(userId, data) {
  if (!userId) {
    console.error('❌ [IndexedDB] saveUserData: userId es requerido');
    return;
  }

  const db = await getDB();
  const record = { id: String(userId), ...data, _indexedDB_updatedAt: new Date().toISOString() };

  try {
    await db.put(STORES.USER_DATA, record);
  } catch (error) {
    console.error('❌ [IndexedDB] Error guardando user_data:', error.message);
    throw error;
  }
}

/**
 * Lee los datos completos del usuario desde IndexedDB.
 * 🔥 Equivalente directo a: JSON.parse(localStorage.getItem(`imoveispro_db_user_${userId}`))
 * 
 * @param {string} userId - ID del usuario
 * @returns {Promise<object|null>} Datos del usuario o null si no existe
 */
export async function getUserData(userId) {
  if (!userId) {
    console.error('❌ [IndexedDB] getUserData: userId es requerido');
    return null;
  }

  try {
    const db = await getDB();
    const record = await db.get(STORES.USER_DATA, String(userId));
    return record || null;
  } catch (error) {
    console.error('❌ [IndexedDB] Error leyendo user_data:', error.message);
    return null;
  }
}

/**
 * Elimina los datos de un usuario (útil para logout/cleanup).
 * 
 * @param {string} userId - ID del usuario
 * @returns {Promise<void>}
 */
export async function deleteUserData(userId) {
  if (!userId) return;

  try {
    const db = await getDB();
    await db.delete(STORES.USER_DATA, String(userId));
  } catch (error) {
    console.warn('⚠️ [IndexedDB] Error eliminando user_data:', error.message);
  }
}

// ═══════════════════════════════════════════════════════════════
// 📝 SYNC LOGS — Reemplaza conflict_log_*, sync_log_*, etc.
// ═══════════════════════════════════════════════════════════════

/**
 * Guarda una entrada de log en IndexedDB.
 * 🔥 Equivalente a: localStorage.setItem(`conflict_log_${userId}`, JSON.stringify(data))
 * 
 * @param {string} userId - ID del usuario
 * @param {string} type - Tipo de log (conflict, sync, error, etc.)
 * @param {object} data - Datos del log
 * @returns {Promise<number>} ID del log creado
 */
export async function saveLog(userId, type, data) {
  if (!userId || !type) return null;

  const db = await getDB();
  const entry = {
    userId: String(userId),
    type,
    data: typeof data === 'string' ? data : JSON.stringify(data),
    timestamp: new Date().toISOString(),
    _timestamp: Date.now(),
  };

  try {
    const id = await db.add(STORES.SYNC_LOGS, entry);

    // Limpieza automática si hay demasiados logs (asíncrona)
    const count = await db.count(STORES.SYNC_LOGS);
    if (count > MAX_LOG_ENTRIES * 2) {
      cleanExpiredLogs().catch(() => {});
    }

    return id;
  } catch (error) {
    console.error('❌ [IndexedDB] Error guardando log:', error.message);
    return null;
  }
}

/**
 * Obtiene logs filtrados por userId y tipo.
 * 
 * @param {string} userId - ID del usuario
 * @param {string} [type] - Tipo de log (opcional, si no se especifica trae todos)
 * @param {number} [limit=50] - Cantidad máxima de logs
 * @returns {Promise<Array>} Lista de logs
 */
export async function getLogs(userId, type = null, limit = 50) {
  if (!userId) return [];

  try {
    const db = await getDB();
    const tx = db.transaction(STORES.SYNC_LOGS, 'readonly');
    const index = tx.store.index('userId');
    let cursor = await index.openCursor(String(userId), 'prev'); // Más recientes primero
    const results = [];

    while (cursor && results.length < limit) {
      const log = cursor.value;
      if (!type || log.type === type) {
        results.push(log);
      }
      cursor = await cursor.continue();
    }

    await tx.done;
    return results;
  } catch (error) {
    console.error('❌ [IndexedDB] Error leyendo logs:', error.message);
    return [];
  }
}

/**
 * Guarda logs de conflicto (interfaz específica para ConflictResolver).
 * 🔥 Equivalente a: _safeSetItem(`conflict_log_${userId}`, JSON.stringify(data))
 * 
 * @param {string} key - Clave del log (ej: conflict_log_user123)
 * @param {string} value - Valor JSON stringificado
 * @returns {Promise<boolean>} true si se guardó correctamente
 */
export async function saveConflictLogEntry(key, value) {
  try {
    const db = await getDB();
    // Extraer userId de la key (formato: conflict_log_<userId>)
    const userId = key.replace(/^(conflict_log_|manual_conflicts_)/, '');
    const type = key.startsWith('manual_conflicts_') ? 'manual_conflict' : 'conflict';

    const entry = {
      userId,
      type,
      key,
      data: value,
      timestamp: new Date().toISOString(),
      _timestamp: Date.now(),
    };

    await db.add(STORES.SYNC_LOGS, entry);

    // Mantener solo los últimos MAX_LOG_ENTRIES de este tipo
    const allLogs = await db.getAllFromIndex(STORES.SYNC_LOGS, 'userId', userId);
    const typeLogs = allLogs.filter(l => l.type === type).sort((a, b) => b._timestamp - a._timestamp);

    if (typeLogs.length > MAX_LOG_ENTRIES) {
      const toDelete = typeLogs.slice(MAX_LOG_ENTRIES);
      const tx = db.transaction(STORES.SYNC_LOGS, 'readwrite');
      for (const log of toDelete) {
        await tx.store.delete(log.id);
      }
      await tx.done;
    }

    return true;
  } catch (error) {
    console.error('❌ [IndexedDB] Error guardando conflict log:', error.message);
    return false;
  }
}

/**
 * Carga logs de conflicto (interfaz específica para ConflictResolver).
 * 🔥 Equivalente a: JSON.parse(localStorage.getItem(`conflict_log_${userId}`))
 * 
 * @param {string} key - Clave del log (ej: conflict_log_user123)
 * @returns {Promise<object|null>} Datos del log o null
 */
export async function getConflictLogEntry(key) {
  try {
    const db = await getDB();
    const userId = key.replace(/^(conflict_log_|manual_conflicts_)/, '');
    const allLogs = await db.getAllFromIndex(STORES.SYNC_LOGS, 'userId', userId);
    
    // Buscar el log más reciente que coincida con la key
    const matching = allLogs
      .filter(l => l.key === key)
      .sort((a, b) => b._timestamp - a._timestamp);

    if (matching.length > 0) {
      try {
        return JSON.parse(matching[0].data);
      } catch {
        return { log: [] };
      }
    }
    return null;
  } catch (error) {
    console.error('❌ [IndexedDB] Error leyendo conflict log:', error.message);
    return null;
  }
}

/**
 * Limpia logs expirados (más de MAX_LOG_AGE_DAYS días).
 * Se ejecuta automáticamente al inicializar la DB.
 * 
 * @returns {Promise<number>} Cantidad de logs eliminados
 */
export async function cleanExpiredLogs() {
  try {
    const db = await getDB();
    const cutoff = Date.now() - (MAX_LOG_AGE_DAYS * 24 * 60 * 60 * 1000);
    const allLogs = await db.getAll(STORES.SYNC_LOGS);
    
    const expired = allLogs.filter(log => log._timestamp < cutoff);
    
    if (expired.length > 0) {
      const tx = db.transaction(STORES.SYNC_LOGS, 'readwrite');
      for (const log of expired) {
        await tx.store.delete(log.id);
      }
      await tx.done;
      console.log(`🧹 [IndexedDB] ${expired.length} logs expirados eliminados`);
    }
    
    return expired.length;
  } catch (error) {
    console.warn('⚠️ [IndexedDB] Error limpiando logs:', error.message);
    return 0;
  }
}

// ═══════════════════════════════════════════════════════════════
// 📁 FILES — Archivos base64 en chunks de 1MB
// ═══════════════════════════════════════════════════════════════

/**
 * Guarda un archivo base64 en chunks.
 * 
 * @param {string} fileId - ID único del archivo (ej: doc_123)
 * @param {string} base64Data - Datos en base64
 * @param {object} [metadata] - Metadatos adicionales (nombre, tipo, tamaño)
 * @returns {Promise<boolean>} true si se guardó correctamente
 */
export async function saveFile(fileId, base64Data, metadata = {}) {
  if (!fileId || !base64Data) return false;

  try {
    const db = await getDB();
    const chunks = [];
    
    // Dividir en chunks de 1MB
    for (let i = 0; i < base64Data.length; i += FILE_CHUNK_SIZE) {
      chunks.push(base64Data.substring(i, i + FILE_CHUNK_SIZE));
    }

    if (chunks.length > MAX_FILE_CHUNKS) {
      console.error(`❌ [IndexedDB] Archivo demasiado grande: ${chunks.length} chunks (máx ${MAX_FILE_CHUNKS})`);
      return false;
    }

    const tx = db.transaction(STORES.FILES, 'readwrite');

    // Guardar metadatos en el chunk 0
    await tx.store.put({
      chunkId: `${fileId}_meta`,
      fileId,
      chunkIndex: -1,
      totalChunks: chunks.length,
      metadata: {
        ...metadata,
        size: base64Data.length,
        savedAt: new Date().toISOString(),
      },
      isMetadata: true,
    });

    // Guardar cada chunk
    for (let i = 0; i < chunks.length; i++) {
      await tx.store.put({
        chunkId: `${fileId}_chunk_${i}`,
        fileId,
        chunkIndex: i,
        data: chunks[i],
        isMetadata: false,
      });
    }

    await tx.done;
    return true;
  } catch (error) {
    console.error('❌ [IndexedDB] Error guardando archivo:', error.message);
    return false;
  }
}

/**
 * Lee un archivo base64 completo desde IndexedDB.
 * 
 * @param {string} fileId - ID del archivo
 * @returns {Promise<{data: string, metadata: object}|null>} Archivo reconstruido o null
 */
export async function getFile(fileId) {
  if (!fileId) return null;

  try {
    const db = await getDB();
    const allChunks = await db.getAll(STORES.FILES);

    // Buscar metadatos
    const meta = allChunks.find(c => c.chunkId === `${fileId}_meta`);
    if (!meta) return null;

    // Reconstruir archivo
    const dataChunks = allChunks
      .filter(c => c.fileId === fileId && !c.isMetadata)
      .sort((a, b) => a.chunkIndex - b.chunkIndex);

    const data = dataChunks.map(c => c.data).join('');

    return {
      data,
      metadata: meta.metadata,
    };
  } catch (error) {
    console.error('❌ [IndexedDB] Error leyendo archivo:', error.message);
    return null;
  }
}

/**
 * Elimina un archivo y todos sus chunks.
 * 
 * @param {string} fileId - ID del archivo
 * @returns {Promise<boolean>} true si se eliminó correctamente
 */
export async function deleteFile(fileId) {
  if (!fileId) return false;

  try {
    const db = await getDB();
    const allChunks = await db.getAll(STORES.FILES);
    const toDelete = allChunks.filter(c => c.fileId === fileId);

    if (toDelete.length === 0) return false;

    const tx = db.transaction(STORES.FILES, 'readwrite');
    for (const chunk of toDelete) {
      await tx.store.delete(chunk.chunkId);
    }
    await tx.done;
    return true;
  } catch (error) {
    console.error('❌ [IndexedDB] Error eliminando archivo:', error.message);
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════
// 🔄 MIGRACIÓN DESDE LOCALSTORAGE (Ejecutar una sola vez)
// ═══════════════════════════════════════════════════════════════

/**
 * Migra todos los datos de localStorage a IndexedDB.
 * 🔥 NO borra localStorage — solo copia. El borrado es manual después de verificar.
 * 
 * @returns {Promise<{migrated: number, skipped: number, errors: number}>} Resultado de la migración
 */
export async function migrateFromLocalStorage() {
  const result = { migrated: 0, skipped: 0, errors: 0 };
  
  if (typeof window === 'undefined' || !window.localStorage) {
    console.warn('⚠️ [IndexedDB] localStorage no disponible para migración');
    return result;
  }

  console.log('🔄 [IndexedDB] Iniciando migración desde localStorage...');

  // 1. Migrar datos de usuario (imoveispro_db_user_*)
  const userKeys = Object.keys(localStorage).filter(k => k.startsWith('imoveispro_db_user_'));
  
  for (const key of userKeys) {
    try {
      const userId = key.replace('imoveispro_db_user_', '');
      const raw = localStorage.getItem(key);
      if (!raw) { result.skipped++; continue; }

      const data = JSON.parse(raw);
      
      // Verificar si ya existe en IndexedDB
      const existing = await getUserData(userId);
      if (existing) {
        console.log(`⏭️ [IndexedDB] Usuario ${userId} ya existe en IndexedDB, omitiendo`);
        result.skipped++;
        continue;
      }

      await saveUserData(userId, data);
      console.log(`✅ [IndexedDB] Migrado: usuario ${userId}`);
      result.migrated++;
    } catch (error) {
      console.error(`❌ [IndexedDB] Error migrando ${key}:`, error.message);
      result.errors++;
    }
  }

  // 2. Migrar logs de conflicto
  const logKeys = Object.keys(localStorage).filter(k => 
    k.startsWith('conflict_log_') || k.startsWith('manual_conflicts_')
  );

  for (const key of logKeys) {
    try {
      const value = localStorage.getItem(key);
      if (!value) continue;

      await saveConflictLogEntry(key, value);
      result.migrated++;
    } catch (error) {
      console.error(`❌ [IndexedDB] Error migrando log ${key}:`, error.message);
      result.errors++;
    }
  }

  // 3. Migrar operaciones offline
  try {
    const offlineOps = localStorage.getItem('offline_operations');
    if (offlineOps) {
      const userId = 'system'; // Las operaciones offline no tienen userId específico
      await saveLog(userId, 'offline_operations', offlineOps);
      result.migrated++;
    }
  } catch (error) {
    console.error('❌ [IndexedDB] Error migrando offline_operations:', error.message);
    result.errors++;
  }

  console.log(`✅ [IndexedDB] Migración completada: ${result.migrated} migrados, ${result.skipped} omitidos, ${result.errors} errores`);
  return result;
}

/**
 * Verifica si la migración desde localStorage ya se realizó.
 * 
 * @returns {Promise<boolean>} true si ya hay datos en IndexedDB
 */
export async function isMigrationDone() {
  try {
    const db = await getDB();
    const count = await db.count(STORES.USER_DATA);
    return count > 0;
  } catch {
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════
// 🛠️ UTILITARIOS
// ═══════════════════════════════════════════════════════════════

/**
 * Obtiene estadísticas de uso de IndexedDB.
 * 
 * @returns {Promise<object>} Estadísticas de la base de datos
 */
export async function getStorageStats() {
  try {
    const db = await getDB();
    const [userDataCount, logCount, fileCount] = await Promise.all([
      db.count(STORES.USER_DATA),
      db.count(STORES.SYNC_LOGS),
      db.count(STORES.FILES),
    ]);

    // Estimar tamaño total (aproximado)
    let estimatedSize = 0;
    try {
      const allUserData = await db.getAll(STORES.USER_DATA);
      estimatedSize += JSON.stringify(allUserData).length;

      const allLogs = await db.getAll(STORES.SYNC_LOGS);
      estimatedSize += JSON.stringify(allLogs).length;
    } catch {
      // No se puede estimar, continuar
    }

    return {
      database: DB_NAME,
      version: DB_VERSION,
      stores: {
        user_data: { count: userDataCount },
        sync_logs: { count: logCount },
        files: { count: fileCount },
      },
      estimatedSizeBytes: estimatedSize,
      estimatedSizeMB: (estimatedSize / (1024 * 1024)).toFixed(2),
    };
  } catch (error) {
    console.error('❌ [IndexedDB] Error obteniendo estadísticas:', error.message);
    return null;
  }
}

/**
 * Cierra la conexión a la base de datos.
 * Útil para pruebas o cuando el usuario hace logout.
 */
export function closeDB() {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
    dbInitPromise = null;
    console.log('🔒 [IndexedDB] Conexión cerrada');
  }
}

// ═══════════════════════════════════════════════════════════════
// 🩺 HEALTH CHECK
// ═══════════════════════════════════════════════════════════════

/**
 * Verifica que IndexedDB esté funcionando correctamente.
 * 
 * @returns {Promise<object>} Estado de salud
 */
export async function healthCheck() {
  try {
    if (typeof indexedDB === 'undefined') {
      return { status: 'unavailable', error: 'IndexedDB no soportado' };
    }

    const db = await getDB();
    const stores = db.objectStoreNames;
    
    // Verificar que todas las stores existan
    const missingStores = Object.values(STORES).filter(s => !stores.contains(s));

    return {
      status: missingStores.length === 0 ? 'healthy' : 'degraded',
      database: DB_NAME,
      version: DB_VERSION,
      stores: {
        user_data: stores.contains(STORES.USER_DATA),
        sync_logs: stores.contains(STORES.SYNC_LOGS),
        files: stores.contains(STORES.FILES),
      },
      missingStores,
      connected: !!dbInstance,
    };
  } catch (error) {
    return {
      status: 'error',
      error: error.message,
    };
  }
}

console.log('📦 [IndexedDB] Módulo cargado — Listo para usar');
